<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { session } from "@/session";

const route = useRoute();
const router = useRouter();

const pageTitle = computed(() => (route.meta.title as string | undefined) ?? "Dashboard");

// Signed-out screens get a centred card with no navigation. Rendering the shell
// around a login form implies there is something behind it to come back to.
const isAuthLayout = computed(() => route.meta.layout === "auth");

const activeOrg = session.activeOrg;
const orgMenuOpen = ref(false);

type Theme = "dark" | "light";

function storedTheme(): Theme {
  const attribute = document.documentElement.getAttribute("data-theme");
  if (attribute === "light" || attribute === "dark") {
    return attribute;
  }
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

const theme = ref<Theme>(storedTheme());

function toggleTheme() {
  theme.value = theme.value === "dark" ? "light" : "dark";
  document.documentElement.setAttribute("data-theme", theme.value);
  try {
    localStorage.setItem("sudo.theme", theme.value);
  } catch {
    // Private browsing blocks writes. The in-session toggle still works.
  }
}

function switchOrg(orgId: string) {
  session.setActiveOrg(orgId);
  orgMenuOpen.value = false;
}

async function signOut() {
  await session.logout();
  await router.replace({ name: "login" });
}
</script>

<template>
  <!-- Nothing renders until the session is resolved, so a signed-in user never
       sees the login screen flash on refresh. -->
  <div v-if="!session.state.ready" class="boot">
    <span class="boot-mark" aria-hidden="true">S</span>
    <span class="visually-hidden">Loading</span>
  </div>

  <div v-else-if="isAuthLayout" class="auth-shell">
    <div class="auth-brand">
      <span class="brand-mark" aria-hidden="true">S</span>
      <span>
        <span class="brand-text">SUDO</span>
        <span class="brand-sub">Game Servers</span>
      </span>
    </div>
    <RouterView />
  </div>

  <div v-else class="shell">
    <aside class="sidebar">
      <div class="brand">
        <div class="brand-mark" aria-hidden="true">S</div>
        <div>
          <div class="brand-text">SUDO</div>
          <div class="brand-sub">Game Servers</div>
        </div>
      </div>

      <div class="org-switch">
        <button
          class="org-button"
          type="button"
          :aria-expanded="orgMenuOpen"
          @click="orgMenuOpen = !orgMenuOpen"
        >
          <span class="org-name">{{ activeOrg?.name ?? "No organisation" }}</span>
          <span class="org-role">{{ activeOrg?.role }}</span>
        </button>

        <ul v-if="orgMenuOpen" class="org-menu">
          <li v-for="membership in session.state.memberships" :key="membership.orgId">
            <button type="button" class="org-menu-item" @click="switchOrg(membership.orgId)">
              {{ membership.name }}
              <span class="muted">{{ membership.role }}</span>
            </button>
          </li>
        </ul>
      </div>

      <nav class="nav" aria-label="Main">
        <div class="nav-label">Overview</div>
        <RouterLink class="nav-item" to="/">
          <span class="nav-icon" aria-hidden="true">▦</span> Dashboard
        </RouterLink>

        <div class="nav-label">Manage</div>
        <RouterLink class="nav-item" to="/servers">
          <span class="nav-icon" aria-hidden="true">▤</span> Servers
        </RouterLink>
        <RouterLink class="nav-item" to="/players">
          <span class="nav-icon" aria-hidden="true">◉</span> Players
        </RouterLink>
        <RouterLink class="nav-item" to="/bans">
          <span class="nav-icon" aria-hidden="true">⊘</span> Bans
        </RouterLink>
        <RouterLink class="nav-item" to="/agents">
          <span class="nav-icon" aria-hidden="true">▥</span> Agents
        </RouterLink>
        <RouterLink class="nav-item" to="/downloads">
          <span class="nav-icon" aria-hidden="true">⤓</span> Downloads
        </RouterLink>

        <div class="nav-label">Account</div>
        <!-- Shown to every role, not only to owners. The endpoint behind it is
             gated on server.view rather than on billing, because "how many
             servers may we have" is the question a viewer's disabled Add button
             has to be able to answer. -->
        <RouterLink class="nav-item" to="/plan">
          <span class="nav-icon" aria-hidden="true">◈</span> Plan
        </RouterLink>
      </nav>

      <div class="sidebar-foot">
        <div class="who">{{ session.state.displayName }}</div>
        <div class="who-sub">{{ session.state.email }}</div>
        <button class="btn btn-small" type="button" @click="signOut">Sign out</button>
      </div>
    </aside>

    <div class="main">
      <header class="topbar">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <button
          class="btn"
          type="button"
          :aria-label="`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`"
          @click="toggleTheme"
        >
          {{ theme === "dark" ? "☾ Night ops" : "☀ Field manual" }}
        </button>
      </header>

      <main class="content">
        <RouterView />
      </main>
    </div>
  </div>
</template>
